import { AfterViewInit, Component, OnInit, OnChanges, OnDestroy, Input, Output, EventEmitter, ChangeDetectorRef, SimpleChanges, ChangeDetectionStrategy, HostBinding, ViewChild, ElementRef, output } from '@angular/core';
import { BehaviorSubject, merge, Subject } from 'rxjs';
import { StringPropertyViewModel } from '../../../view-models/base-type/string-property-view-model';
import { BaseNumericPropertyViewModel } from '../../../view-models/base-type/base-numeric-property-view-model';
import { BaseEnumPropertyViewModel } from '../../../view-models/base-type/enum-property-view-model';
import { BaseBoolPropertyViewModel } from '../../../view-models/base-type/bool-property-view-model';
import { BaseDateTimePropertyViewModel } from '../../../view-models/base-type/date-time-property-view-model';
import { BaseOffsetPropertyViewModel } from '../../../view-models/base-type/offset-property-view-model';
import { BaseDateTimeOffsetPropertyViewModel, DateTimeOffsetPropertyViewModel } from '../../../view-models/base-type/date-time-offset-property-view-model';
import { GuidPropertyViewModel } from '../../../view-models/base-type/guid-property-view-model';
import { SimpleFieldComponent } from '../core/simple-field/simple-field.component';
import { takeUntil } from 'rxjs/operators';
import { NgxPopperjsPlacements, NgxPopperjsTriggers } from 'ngx-popperjs';
import { BaseTimeSpanPropertyViewModel, TimeSpanPropertyViewModel } from '../../../view-models/base-type/time-span-property-view-model';
import { LabelBoxComponent } from '../core/base/label-box/label-box.component';
import { NumericTextBoxComponent } from '../core/numeric-text-box/numeric-text-box.component';
import { EnumOptionsInterface, EnumTextBoxComponent } from '../core/enum-text-box/enum-text-box.component';
import { RadioButtonTextBoxComponent } from '../core/radio-button-text-box/radio-button-text-box.component';
import { ToggleTextBoxComponent } from '../core/toggle-text-box/toggle-text-box.component';
import { CheckboxTextBoxComponent } from '../core/checkbox-text-box/checkbox-text-box.component';
import { DateTextBoxComponent } from '../core/date-text-box/date-text-box.component';
import { StringTextBoxComponent } from '../core/string-text-box/string-text-box.component';
import { TimeSpanTextBoxComponent } from '../core/time-span-text-box/time-span-text-box.component';
import { DateOffsetTextBoxComponent } from '../core/date-offset-text-box/date-offset-text-box.component';
import { AsyncPipe } from '@angular/common';
import { ChipsOptionsInterface } from '../core/base/base-chips-box/base-chips-box.component';
import type { BaseErrorInterface, PropertyViewModelInterface, PropertyViewModelPropertyChangedEventArgsInterface } from '@nts/std/interfaces';
import { FilePropertyViewModel } from '../../../view-models/base-type/file-property-view-model';
import { FileTextBoxComponent } from '../core/file-text-box/file-text-box.component';
import { ViewReadyCoordinator } from '../core/view-ready-coordinator';
import { BaseError } from '@nts/std/messages';

@Component({
    selector: 'nts-labelled-text-box',
    templateUrl: './labelled-text-box.component.html',
    styleUrls: ['./labelled-text-box.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LabelBoxComponent,
        NumericTextBoxComponent,
        EnumTextBoxComponent,
        RadioButtonTextBoxComponent,
        ToggleTextBoxComponent,
        CheckboxTextBoxComponent,
        DateTextBoxComponent,
        StringTextBoxComponent,
        TimeSpanTextBoxComponent,
        DateOffsetTextBoxComponent,
        FileTextBoxComponent,
        AsyncPipe,
    ]
})
export class LabelledTextBoxComponent extends SimpleFieldComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {

  /**
   * Property valorizzata solo dal layout custom, vincerà sul pvm.isVisible solo se sarà impostata a false
   */
  @Input() override isHidden = false;

  @HostBinding('class.additional-field')
  @Input() override additionalField = false;

  @HostBinding('class.full-column')
  @Input() fullColumn = false;

  @Input() useToggle = false;

  // #region CHIPS
  @Input() useChips = false;
  @Input() chipsOptions: ChipsOptionsInterface<{ code: string | number, description?: string; }> = null;
  // #endregion CHIPS

  @HostBinding('class.multiline')
  @Input() multiline = false;

  @Input() labelPosition = '';
  @Input() propertyViewModel!: PropertyViewModelInterface;
  @Input() tabIndex!: number;
  @Input() appendToBody = true;
  @Input() isDisabled = false;
  @Input() showLabelRequired = false;
  @Input() primaryColor = null;

  @Input() enumOptions: EnumOptionsInterface = null;

  /**
   * lista nome comandi impostati sul propertyViewModel corrente
   */
  @Input() customCommandsName: string[] | null = null;

  /**
   * Property per ridefinire il path per il layout.
   */
  @Input() customPathForLayout: string = null;

  @Output() propertyViewModelInitialized = new EventEmitter<void>();
  readonly viewReady = output<void>();
  isViewReady = false;

  @Input() labelId?: string;
  @ViewChild('labelledContainer', { static: false }) labelledContainer: ElementRef;
  private ariaLabelApplied = false;

  /**
   * Property calcolata in base alle due property: isHidden e pvm.isVisible
   */
  @HostBinding('class.is-hidden')
  isHiddenCalculated = false;

  enumTextBoxComponent$: BehaviorSubject<EnumTextBoxComponent> = new BehaviorSubject<EnumTextBoxComponent | null>(null);

  @ViewChild(EnumTextBoxComponent, { static: false })
  set enumTextBoxComponent(component: EnumTextBoxComponent | null) {
    if (component && component !== this.enumTextBoxComponent$.value) {
      component.parentComponent = this;
      this.enumTextBoxComponent$.next(component);
    }

    this.tryEmitViewReady();
  }
  get enumTextBoxComponent() {
    return this.enumTextBoxComponent$.value;
  }

  loadingError: BaseErrorInterface | null = null;
  ngxPopperjsTriggers = NgxPopperjsTriggers;
  ngxPopperjsPlacements = NgxPopperjsPlacements;
  private readonly viewReadyCoordinator = new ViewReadyCoordinator(['self', 'child'] as const, () => this.emitViewReady());

  private nameFromInput = undefined;

  @Input()
  set name(value: string) {
    this.nameFromInput = value;
  }
  get name(): string {
    if (this.nameFromInput != null) {
      return this.nameFromInput;
    }
    return this.propertyViewModel?.propertyName ?? '';
  }

  private pathFromInput = undefined;

  @Input()
  set path(value: string) {
    this.pathFromInput = value;
  }
  get path(): string {
    if (this.pathFromInput != null) {
      return this.pathFromInput;
    }
    if (this.customPathForLayout != null) {
      return this.customPathForLayout;
    }
    if (this.propertyViewModel?.customPathForLayout != null) {
      return this.propertyViewModel.customPathForLayout;
    }
    return this.propertyViewModel?.propertyPath ?? '';
  }

  get description(): string {
    return this.propertyViewModel?.metadataDescription;
  }

  get displayName(): string {
    return this.propertyViewModel?.metadataShortDescription;
  }

  get isString() {
    return this.propertyViewModel && this.propertyViewModel instanceof StringPropertyViewModel && !(this.propertyViewModel instanceof FilePropertyViewModel);
  }

  get isTimeSpan() {
    return this.propertyViewModel && this.propertyViewModel instanceof BaseTimeSpanPropertyViewModel;
  }

  get isGuid() {
    return this.propertyViewModel && this.propertyViewModel instanceof GuidPropertyViewModel;
  }

  get stringPropertyViewModel() {
    return this.propertyViewModel && this.propertyViewModel as StringPropertyViewModel;
  }

  get filePropertyViewModel() {
    return this.propertyViewModel && this.propertyViewModel as FilePropertyViewModel;
  }

  get isFile() {
    return this.propertyViewModel && this.propertyViewModel instanceof FilePropertyViewModel;
  }

  get isDate() {
    return this.propertyViewModel && this.propertyViewModel instanceof BaseDateTimePropertyViewModel;
  }

  get datePropertyViewModel() {
    return this.propertyViewModel && this.propertyViewModel as BaseDateTimePropertyViewModel;
  }

  get isNumeric() {
    return this.propertyViewModel && this.propertyViewModel instanceof BaseNumericPropertyViewModel;
  }

  get numericPropertyViewModel() {
    return this.propertyViewModel && this.propertyViewModel as BaseNumericPropertyViewModel;
  }

  get isOffset() {
    return this.propertyViewModel && this.propertyViewModel instanceof BaseOffsetPropertyViewModel;
  }

  get isDateTimeOffset() {
    return this.propertyViewModel && this.propertyViewModel instanceof BaseDateTimeOffsetPropertyViewModel;
  }

  get dateTimeOffsetPropertyViewModel() {
    return this.propertyViewModel as DateTimeOffsetPropertyViewModel;
  }

  get timeSpanPropertyViewModel() {
    return this.propertyViewModel as TimeSpanPropertyViewModel;
  }

  get isBool() {
    return this.propertyViewModel instanceof BaseBoolPropertyViewModel;
  }

  get isEnum() {
    return this.propertyViewModel instanceof BaseEnumPropertyViewModel && !this.multiline;
  }

  get isRadio() {
    return this.propertyViewModel instanceof BaseEnumPropertyViewModel && this.multiline;
  }

  get enumPropertyViewModel() {
    return this.propertyViewModel as BaseEnumPropertyViewModel;
  }

  get isFullWidth() {
    // deroga per retrocompatibilita multiline, se non viene passata la position
    // la considero sempre full-widht
    if (this.isString && this.multiline && this.labelPosition === '') {
      return true;
    }
    return (this.labelPosition === 'top' ? true : false);
  }

  protected destroy$: Subject<boolean> = new Subject<boolean>();
  protected destroyOnChange$: Subject<boolean> = new Subject<boolean>();

  constructor(
    protected readonly cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    if (this.propertyViewModel == null) {
      this.loadingError = new BaseError();
      this.loadingError.description = `Errore caricamento componente ${[this.path, this.name].filter(a => a?.length > 0).join('.')}`;
      this.loadingError.code = 'LabelledTextBox_MissingPropertyViewModel';
      return;
    }
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => {
      this.viewReadyCoordinator.markReady('self');

      if (!this.requiresEnumChildReady()) {
        this.viewReadyCoordinator.markReady('child');
      }

      this.tryEmitViewReady();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isHidden']) {
      if (changes['isHidden']?.currentValue === true) {
        this.isHiddenCalculated = true;
      } else {
        if (this.propertyViewModel != null) {
          this.isHiddenCalculated = !this.propertyViewModel?.isVisible;
        }
      }
    } else if (changes['propertyViewModel']) {
      this.destroyOnChange$.next(true);

      if (this.propertyViewModel != null) {

        this.isHiddenCalculated = this.isHidden === true ? true : !this.propertyViewModel?.isVisible;

        this.propertyViewModel?.componentIstance$.next(this);

        // Rimane in ascolto quando cambia il pvm e lo stato degli errori
        merge(
          this.propertyViewModel.customGetterValueChanged,
          this.propertyViewModel.propertyViewModelChanged,
          this.propertyViewModel.onErrorStatusChanged
        ).pipe(
          takeUntil(this.destroy$),
          takeUntil(this.destroyOnChange$)
        ).subscribe((x) => {
          this.cd.markForCheck();
        });

        merge(
          this.propertyViewModel.onFocusRequested,
        ).pipe(
          takeUntil(this.destroy$),
          takeUntil(this.destroyOnChange$)
        ).subscribe((x) => {
          this.additionalField = false;
          this.isHidden = false;
          this.cd.markForCheck();
        });

        // Rimane in ascolto quando cambiano gli attributi del pvm
        this.propertyViewModel.propertyChanged.pipe(
          takeUntil(this.destroy$),
          takeUntil(this.destroyOnChange$)
        ).subscribe((arg: PropertyViewModelPropertyChangedEventArgsInterface<any>) => {
          if (arg.propertyName === 'isVisible') {
            this.isHiddenCalculated = this.isHidden === true ? true : !this.propertyViewModel.isVisible;
          }
          this.cd.markForCheck();
        });

        this.propertyViewModelInitialized.emit();
        // generate a deterministic label id when not provided
        if (!this.labelId) {
          let raw = `${this.name}`;
          if (this.path?.length > 0) {
            raw = `${this.path}-${this.name}`;
          }
          this.labelId = this.sanitizeId(raw);
        }
      }
    }
  }

  ngOnDestroy() {
    this.viewReadyCoordinator.destroy();

    this.chipsOptions = null;
    this.propertyViewModel = null;

    this.enumOptions = null;

    this.customCommandsName = null;

    this.propertyViewModelInitialized?.complete();
    this.propertyViewModelInitialized = null;

    this.enumTextBoxComponent$?.complete();
    this.enumTextBoxComponent$ = null;

    this.destroyOnChange$?.next(true);
    this.destroyOnChange$?.complete();
    this.destroyOnChange$ = null;

    this.destroy$?.next(true);
    this.destroy$?.complete();
    this.destroy$ = null;
  }

  viewReadyCheck(): void {
    this.viewReadyCoordinator.markReady('child');
    this.tryEmitViewReady();
    // After children are ready, apply aria-labelledby to the actual input elements
    setTimeout(() => this.applyAriaLabelled(), 0);
  }

  private requiresEnumChildReady(): boolean {
    return this.isEnum || this.isOffset;
  }

  private tryEmitViewReady(): void {
    this.viewReadyCoordinator.tryEmit(() => !this.requiresEnumChildReady() || this.enumTextBoxComponent != null);
  }

  private emitViewReady(): void {
    this.isViewReady = true;
    this.viewReady.emit();
  }

  private sanitizeId(raw: string): string {
    if (!raw) { return 'lbl-' + Math.random().toString(36).slice(2, 8); }
    let id = raw.replace(/[^a-zA-Z0-9_-]/g, '-');
    id = id.replace(/-+/g, '-');
    return `lbl-${id}`;
  }

  private applyAriaLabelled(): void {
    if (this.ariaLabelApplied || !this.labelId) { return; }
    const containerEl: HTMLElement = this.labelledContainer?.nativeElement;
    if (!containerEl) { return; }

    const editContainer = containerEl.querySelector('.edit-container') as HTMLElement || containerEl;
    if (!editContainer) { return; }

    const selectors = 'input, textarea, select, [role="checkbox"], [role="radiogroup"], [role="combobox"], [role="listbox"], [role="textbox"], .p-inputtext, .p-chips, .p-multiselect, .p-dropdown, .p-calendar';
    const els = Array.from(editContainer.querySelectorAll(selectors));
    els.forEach((el: Element) => {
      try {
        if (!el.hasAttribute('aria-labelledby')) {
          el.setAttribute('aria-labelledby', this.labelId);
        }
      } catch (e) {
        // ignore
      }
    });

    this.ariaLabelApplied = true;
  }
}
